import axios, { AxiosError } from "axios";
import {
  UseMutationResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Asset, TourData, Scene, Group, Hotspot, Popup } from "../../Types";
import {
  useCurrentSceneStore,
  useDataRootStore,
  useLocalizationStore,
  useParametersStore,
} from "../Zustand";
import { axiosInstance, token } from "./Axios";

//LOAD TOUR DATA

const baseTourData: TourData = {
  title: "Nuovo tour",
  fov: 75,
  scenes: [],
  maps: [],
  groups: [
    {
      id: Math.floor(100000 + Math.random() * 900000),
      name: "Primo gruppo",
      scenes: [],
      mainColor: "#bada55",
      textColor: "#222",
    },
  ],
  popups: [],
  isNew: true,
  startingScene: undefined,
  assignedQuizID: undefined,
  quizData: undefined,
};

export const UseGetTour = (tourId: number) => {
  return useQuery<TourData, AxiosError>({
    queryKey: ["GetTour", tourId],
    queryFn: () => GetTour(tourId),

    retry: 1,
  });
};

const GetTour = async (id: number): Promise<TourData> => {
  const axiosResp = await axiosInstance
    .get("Tour/tour/getTour", {
      params: {
        idTour: id,
      },
    })
    .then((resp) => {
      return resp.data;
    })
    .catch((e) => {
      return e;
    });

  if (axiosResp.data.jsonData === "" || axiosResp.data.jsonData === null) {
    return {
      ...baseTourData,
      title: axiosResp.data.title || "Nuovo tour",
      isNew: undefined,
    };
  } else {
    const json = JSON.parse(axiosResp.data.jsonData);

    const generatedBackup = {
      maps: json.maps,
      popups: json.popups,
    };

    const parsedTourData: TourData = {
      ...json,
      zoom: parseInt(json.zoom),
      title: axiosResp.data.title || "Nuovo tour",
      scenes: json?.scenes?.map((sc: Scene) => {
        // let { imageData, imageFull, imageLow, imageThumb, image, ...rest } = sc;

        return {
          ...sc,
          fov: parseInt(sc.fov as unknown as string),
          hotspots: sc.hotspots.map((hs: Hotspot) => {
            let { EDIT_POS, isNew, ...rest } = hs;
            return rest;
          }),
        };
      }),

      groups: json?.groups?.map((group: Group) => {
        return {
          ...group,
          scenes: json.scenes
            .filter((sc: Scene) => sc.group === group.id)
            .map((sc: Scene) => sc.id),
        };
      }),
      backup: json.backup || generatedBackup,
    };

    // previousTourData.push(parsedTourData);

    const { setSceneIDOnLoad } = useCurrentSceneStore.getState();

    setSceneIDOnLoad(parsedTourData.startingScene);

    // setSceneIDOnLoad(parsedTourData.initialScene)
    return parsedTourData;
    // return tempTourData;
  }
};

//SAVE TOUR DATA

export const UseSaveTour = () => {
  const tourId = useParametersStore.getState().tourID;

  const userId = useParametersStore.getState().userID;

  return useMutation({
    mutationFn: (newTourData: TourData) =>
      SaveTour(newTourData, tourId, userId),
  });
};

const SaveTour = async (newTourData: TourData, id: number, userId: number) => {
  const usedPanos = newTourData.scenes
    .map((sc) => sc.imageID)
    .filter((images) => images !== undefined);
  // DELETE UNUSED HOTSPOTS

  const mergedSceneHotspots = newTourData.scenes
    .map((sc: Scene) => sc.hotspots.map((hs) => hs))
    .flat();

  const escapeRoomPopups = [
    newTourData.escapeRoomData?.endPopupID,
    newTourData.escapeRoomData?.startPopupID,
  ];

  const escapeRoomQuestionPopups: (number | undefined)[] = [
    ...(newTourData.escapeRoomData?.escapeRoomQuestions?.map((q) => {
      return q.correctAnsPopID;
    }) ?? []),
    ...(newTourData.escapeRoomData?.escapeRoomQuestions?.map((q) => {
      return q.wrongAnsPopID;
    }) ?? []),
  ];

  const mergedScenePopups = mergedSceneHotspots.filter(
    (hs) => hs.type === "popup"
  );

  const allExistingPopupsIDS = [
    ...mergedScenePopups.map((pop) => {
      return pop.id;
    }),
    ...(escapeRoomPopups ?? []),
    ...(escapeRoomQuestionPopups ?? []),
  ];

  const newPopups = newTourData.popups.filter((pops) =>
    allExistingPopupsIDS.find((one) => one === pops.id)
  );

  const usedQuestions = mergedSceneHotspots
    .filter((hs) => hs.type === "quiz")
    .map((hs) => {
      return hs.target;
    });
  const uniqueUsedQuestions = [...new Set(usedQuestions)].filter(
    (elem) => elem !== undefined
  );

  const usedImagesInPops = [...new Set(newPopups.map((pop) => pop.imageID))];

  const usedImagesInMaps = newTourData.maps
    ?.map((map) => map.assetId)
    .filter((images) => images !== undefined);
  const usedAudiosHotspots = mergedSceneHotspots
    .filter((hs) => hs.type === "audio")
    .map((audio) => audio.target)
    .filter((audios) => audios !== undefined);

  const usedAudios = newTourData.scenes
    .map((sc) => sc.audio)
    .filter((audio) => audio !== undefined);

  const usedAssets = [
    // @ts-expect-error
    ...usedImagesInMaps,
    ...usedImagesInPops,
    ...usedAudiosHotspots,
    ...usedAudios,
  ].filter((elem) => elem !== undefined);

  const quizIds = [newTourData.assignedQuizID].filter(
    (val) => val !== undefined
  );

  const fixedPopups = newTourData.popups.filter((pop) =>
    allExistingPopupsIDS.find((one) => one === pop.id)
  );

  const fixedMaps = newTourData.maps?.map((map) => {
    return {
      ...map,
      points: map.points?.filter((points) =>
        newTourData.scenes.find((scene) => scene.id === points.target)
      ),
    };
  });

  const fixedTourData = {
    ...newTourData,
    popups: fixedPopups,
    maps: fixedMaps,
    usePanos: undefined,
    usedPanos: usedPanos,
    usedAssets: usedAssets,
  };

  const axiosResp = await axiosInstance.post("Tour/tour/postSaveTour", {
    idTour: id,
    jsonData: [
      JSON.stringify(fixedTourData),
      JSON.stringify(usedPanos),
      JSON.stringify(usedAssets),
      JSON.stringify(quizIds),
      JSON.stringify(uniqueUsedQuestions),
    ],
    userId: userId,
  });

  return axiosResp;
};

//IMAGE DOWNLOAD

export const UseDownloadAsset = (
  assetKey: string | undefined,
  setPercentage?: (number?: number) => void
) => {
  return useQuery({
    queryKey: ["DownloadAsset", assetKey],
    queryFn: () => downloadAsset(assetKey, setPercentage),

    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 1,

    enabled: !!assetKey,
    staleTime: Infinity,

    // cacheTime: Infinity,
  });
};

const downloadAsset = async (
  assetKey: string | undefined,
  setPercentage?: (number?: number) => void
) => {
  const axiosResp = await axiosInstance
    .get("Tour/assets/downloadAsset", {
      responseType: "blob",
      params: {
        AssetKey: assetKey,
      },
      onDownloadProgress: (dl) => {
        if (setPercentage) setPercentage(dl.progress);
      },
    })
    .then((res) => {
      if (setPercentage) {
        setPercentage(undefined);
      }
      let blob = new Blob([res.data], {
        type: res.headers["content-type"],
      });

      // @ts-expect-error
      URL.revokeObjectURL(blob);

      let objBlob = URL.createObjectURL(blob);
      return objBlob;
    });
  return axiosResp;
};

//PANO UPLOAD
export const UseSetAsset: (
  assetType: number,
  setProgress?: React.Dispatch<React.SetStateAction<number | undefined>>
) => UseMutationResult<
  any,
  AxiosError,
  {
    file: File;
    assetFileType: string;
    assetId: number | undefined;
  },
  unknown
> = (
  assetType: number,
  setProgress?: React.Dispatch<React.SetStateAction<number | undefined>>
) => {
    return useMutation({
      mutationFn: (props: {
        file: File;
        assetFileType: string;
        assetId: number | undefined;
      }) =>
        setAsset(
          props.file,
          props.assetFileType,
          props.assetId,

          assetType,
          setProgress
        ),
    });
  };

//AXIOS

const setAsset = async (
  file: File,
  assetFileType: string,
  assetId: number | undefined,
  assetType: number,
  setProgress?: React.Dispatch<React.SetStateAction<number | undefined>>
) => {
  const userId = useParametersStore.getState().userID;
  const tourId = useParametersStore.getState().tourID;

  const longDesc = {
    type: file.type,
    lastModified: file.lastModified,
    size: file.size,
  };

  const formData = new FormData();

  const json = {
    AssetId: assetId || 0,
    UserId: userId,
    AssetType: assetType,
    IdTour: tourId,
    AssetFileType: assetFileType,
    AssetShortDescription: assetFileType === "F" ? file.name : undefined,
    AssetExtDescription: JSON.stringify(longDesc),
  };

  formData.append("AssetFile", file);
  formData.append("json", JSON.stringify(json));

  const axiosResp = await axiosInstance
    .post("Tour/assets/SetAsset", formData, {
      headers: {
        "Content-Type":
          "multipart/form-data; boundary=<calculated when request is sent>",
      },
      onUploadProgress: (p) => {
        if (p.total && setProgress) {
          let prog = p.loaded / p.total;
          setProgress(prog);
        }
      },
    })
    .then((res) => {
      // return res;
      return res.data.data[0];
    });
  return axiosResp;
};

export function UseAssetsQuery(tourID: number, assetType: number) {
  // const prova = useParametersStore.getState().tourID;

  const queryClient = useQueryClient();
  return useQuery<Asset[]>({
    queryKey: ["GetAssets", tourID, assetType],
    queryFn: async () => {
      const data = axiosInstance
        .post("Tour/assets/GetAssets", {
          idTour: tourID,
          assetType: assetType,
        })
        .then((resp) => {
          const data = resp?.data?.data;
          if (data) {
            if (assetType === 1) {
              // @ts-ignore
              queryClient.setQueryData(["GetTour", tourID], (old: TourData) => {
                return {
                  ...old,
                  scenes: old?.scenes?.map((sc: Scene) => {
                    return sc.imageID === undefined
                      ? {
                        ...sc,
                        imageID: data.find(
                          // @ts-ignore
                          (one) => one.assetKeyFull === sc.imageFull
                        )?.assetId,
                      }
                      : sc;
                  }),
                };
              });
            } else if (assetType === 2) {
              queryClient.setQueryData(["GetTour", tourID], (old: TourData) => {
                return {
                  ...old,
                  popups: old?.popups?.map((pop: Popup) => {
                    return pop.type === "image"
                      ? {
                        ...pop,
                        imageID: data.find(
                          // @ts-ignore
                          (one) => one.assetKeyFull === pop.image
                        )?.assetId,
                      }
                      : pop;
                  }),
                };
              });
            }
            return data;
          }
        })
        .catch((err) => console.debug(err));

      return data;
    },
    enabled: !!token,
  });
}

export function useDeleteAsset(assetType: 1 | 2 | 3) {
  const tourId = useParametersStore.getState().tourID;

  const { refetch } = UseAssetsQuery(tourId, assetType);

  return useMutation({
    mutationKey: ["DeleteAsset"],
    mutationFn: async (assetID: number) => {
      const payload = { AssetId: assetID, AssetType: assetType };

      return axiosInstance
        .post("Tour/assets/deleteAsset", payload)
        .then((res) => {
          return res;
        })
        .catch((err) => {
          throw err;
        });
    },
    onSuccess: () => {
      refetch();
    },
  });
}

export function useLocalizationFile() {
  const { root } = useDataRootStore();

  const { lang: lang_ID } = useParametersStore();

  const isInDev = process.env.NODE_ENV === "development";

  const localUrl = "./lang/" + lang_ID + ".json";
  const baseUrl = root + "/lang/" + lang_ID + ".json";

  const { setLanguage } = useLocalizationStore();
  return useQuery({
    queryKey: ["FetchLanguageFile", lang_ID],

    queryFn: async () => {
      try {
        const data = await axios.get(isInDev ? localUrl : baseUrl);

        const languageNonProcessed: {
          [key: string]: string;
        } = data.data;
        let processed: { [key: string]: string } = {};

        for (const [key, value] of Object.entries(languageNonProcessed)) {
          const outerKey = key;
          const obj = value;

          for (const [key, value] of Object.entries(obj)) {
            processed[outerKey + "." + key] = value;
          }
        }

        setLanguage(processed);
        return processed;
      } catch (err) {
        throw err;
      }
    },
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    gcTime: 1000 * 60 * 60,
  });
}
